// The three decisions the halt surface makes, as pure functions.
//
// T12 of docs/plans/2026-09-07-verification-overlay.md. They live here rather
// than inside the two `.tsx` files that use them for the reason anchorText.ts
// and panelModel.ts exist: `frontend/vitest.config.ts` is `environment: 'node'`
// with `include: ['src/**/*.test.ts']`, so a `.tsx` test is not collected and
// would report a pass having run nothing. Anything with a decision in it is a
// `.ts` module with real tests; the components stay thin.
//
// NOTHING HERE DECIDES WHETHER TO HALT. That decision is made once, by
// `applyVerificationOutcome` on the real path and by the ledger's sticky
// `LaneSet.mismatch` on the fixture path, and is already tested in
// useVideoVerification.test.ts and lanes.test.ts. These functions only carry an
// already-made decision between the two shapes the two paths hold it in, and
// describe the scrim that draws it.
import type { Mismatch } from './ledger/lanes.js'
import { describeSealedTarget, type VerificationHalt } from './useVideoVerification.js'

/**
 * Fixture path → player. The ledger records a mismatch; `VideoPlayer` enforces
 * a halt and only understands `VerificationHalt`, so the fixture's mismatch is
 * carried across into that shape rather than the player learning a second one.
 *
 * `stage` is always `'segment'`: a fixture mismatch is only ever produced for a
 * slice of media (syntheticLedgerSource's `failSlice`, realLedgerSource's
 * fabricated disagreement). Nothing in the fixture path can fail a root, a
 * rendition manifest or a subtitle, because none of them is fetched.
 */
export function haltFromMismatch(mismatch: Mismatch): VerificationHalt {
  return {
    stage: 'segment',
    detail: mismatch.detail,
    rendition: mismatch.rendition,
    segmentIndex: mismatch.index,
  }
}

/**
 * Player → panel, the other direction, on the real path: `useVideoVerification`
 * holds the halt and the modal shows a `Mismatch`.
 *
 * The sentence `describeSealedTarget` builds is prepended because the panel is
 * now the only place a halted viewer is told anything — the standalone
 * VerificationFailure screen this ticket deletes was where that sentence used
 * to be shown. `halt.detail` follows it rather than replacing it: it is the
 * technical line, and losing it would leave a viewer with no way to say what
 * happened to anyone who could act on it.
 *
 * The hashes stay absent. A `VerificationHalt` does not carry them, and the
 * panel prints "not recorded" rather than inventing a pair — which is the same
 * rule the rest of this module follows about claiming only what was checked.
 */
export function mismatchFromHalt(halt: VerificationHalt): Mismatch {
  return {
    rendition: halt.rendition,
    index: halt.segmentIndex,
    detail: `${describeSealedTarget(halt)} does not match the hash recorded on chain when this video was sealed. ${halt.detail}`,
  }
}
